'use memo'

import { useContext, useEffect, useMemo, useState } from 'react'
import { MenuTitles } from 'constants/menu'
import { AgencyContext } from 'contexts'
import { ContentLayout, PageLayout } from 'layout'
import {
  AgxButton,
  AgxDatePicker,
  AgxDropdown,
  AgxEmptyState,
  AgxSaveChangesButton,
  AgxSpacer,
  AgxTab,
  AgxTextInput,
  AgxTypography,
  SAVE_BUTTON_STATES,
  useToast,
} from '@urbanx/agx.ui-components2'
import {
  AutopilotEntityDetailsModel,
  CompanyEntityDetailsModel,
  LicenceDetails,
  LicenceDetailsState,
  useGetAutopilotEntityDetails,
  useInvalidateUseGetAutopilotEntityDetails,
  useUpdateAutopilotEntityDetails,
} from '@urbanx/agx.agencies.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import { isDirtyEqual } from 'utils/compares'
import { FormPrompt } from 'components/FormPrompt'
import { toDateOnlyString } from 'utils/date'
import {
  createLicenceDetails,
  EntityDetailsTab,
  entityDetailsTabOptions,
  getDetailsForTab,
  getLicenceStateOptions,
  getNextAvailableLicenceState,
  normaliseAutopilotEntityDetails,
  removeLicenceAtIndex,
  stateOptions,
  updateDetailsForTab,
  updateLicenceAtIndex,
} from './helpers'

const StaffLicense = () => {
  const { userAccount } = useContext(AuthContext)
  const [selectedAgency] = useContext(AgencyContext)
  const { pushToast } = useToast()
  const [selectedTab, setSelectedTab] = useState<EntityDetailsTab>(
    EntityDetailsTab.Autopilot
  )
  const [entityDetailsForm, setEntityDetailsForm] = useState<
    AutopilotEntityDetailsModel | undefined
  >()
  const [saveButtonState, setSaveButtonState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  )
  const {
    data: autopilotEntityDetails,
    isPending: autopilotEntityDetailsPending,
    isError: autopilotEntityDetailsError,
  } = useGetAutopilotEntityDetails()
  const invalidateGetAutopilotEntityDetails =
    useInvalidateUseGetAutopilotEntityDetails()

  const { updateAutopilotEntityDetails, updateAutopilotEntityDetailsPending } =
    useUpdateAutopilotEntityDetails({
      onSuccess: async (_: void, inputData: AutopilotEntityDetailsModel) => {
        await invalidateGetAutopilotEntityDetails()
        setEntityDetailsForm(normaliseAutopilotEntityDetails(inputData))
        setSaveButtonState(SAVE_BUTTON_STATES.SAVED)
        pushToast({
          type: 'success',
          message: `${MenuTitles.LICENSEE_DETAILS} updated successfully`,
        })
      },
      onError: () => {
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
        pushToast({
          type: 'error',
          message: `Error updating ${MenuTitles.LICENSEE_DETAILS}`,
        })
      },
    })

  useEffect(() => {
    if (autopilotEntityDetailsPending) return

    setEntityDetailsForm(
      normaliseAutopilotEntityDetails(autopilotEntityDetails)
    )
  }, [autopilotEntityDetails, autopilotEntityDetailsPending])

  const onSaveChanges = async () => {
    if (!entityDetailsForm) return

    setSaveButtonState(SAVE_BUTTON_STATES.SAVING)

    try {
      await updateAutopilotEntityDetails(entityDetailsForm)
    } catch {
      // onError is handled by the generated hook.
    }
  }

  const normalisedServerDetails = normaliseAutopilotEntityDetails(
    autopilotEntityDetails
  )

  const isFormDirty = useMemo(() => {
    if (!entityDetailsForm || !autopilotEntityDetails) return false

    return !isDirtyEqual(entityDetailsForm, normalisedServerDetails)
  }, [entityDetailsForm, autopilotEntityDetails, normalisedServerDetails])

  const saveChangesState = useMemo(() => {
    if (updateAutopilotEntityDetailsPending) return SAVE_BUTTON_STATES.SAVING
    if (saveButtonState === SAVE_BUTTON_STATES.ERROR)
      return SAVE_BUTTON_STATES.ERROR
    if (isFormDirty) return SAVE_BUTTON_STATES.UNSAVED
    return saveButtonState
  }, [isFormDirty, saveButtonState, updateAutopilotEntityDetailsPending])

  const isInitialisingEntityDetailsForm =
    !autopilotEntityDetailsError && entityDetailsForm === undefined

  const selectedEntityDetails = getDetailsForTab(entityDetailsForm, selectedTab)

  const selectedTabLabel =
    entityDetailsTabOptions.find((tab) => tab.id === selectedTab)?.label ??
    'Entity'

  const companyDetails = selectedEntityDetails.companyDetails
  const address = selectedEntityDetails.address
  const licences = companyDetails?.licences ?? []

  const updateSelectedEntityDetails = (
    updater: (details: CompanyEntityDetailsModel) => CompanyEntityDetailsModel
  ) => {
    setEntityDetailsForm((prev) =>
      updateDetailsForTab(
        prev,
        selectedTab,
        updater(getDetailsForTab(prev, selectedTab))
      )
    )
  }

  const updateLicences = (nextLicences: LicenceDetails[]) => {
    updateSelectedEntityDetails((prev) => ({
      ...prev,
      companyDetails: {
        ...prev.companyDetails,
        licences: nextLicences,
      },
    }))
  }

  const addLicence = () => {
    const nextState = getNextAvailableLicenceState(licences)
    updateLicences([...licences, createLicenceDetails(nextState)])
  }

  const removeLicence = (licenceIndex: number) => {
    updateLicences(removeLicenceAtIndex(licences, licenceIndex))
  }

  const updateLicence = (licenceIndex: number, nextLicence: LicenceDetails) => {
    updateLicences(updateLicenceAtIndex(licences, licenceIndex, nextLicence))
  }

  const tabOptions = entityDetailsTabOptions.map((tab) => ({
    ...tab,
    selected: tab.id === selectedTab,
  }))

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.LICENSEE_DETAILS}
      isStaff
    >
      <ContentLayout
        hasSecondaryMenu
        secondaryActiveMenu={MenuTitles.LICENSEE_DETAILS}
        isStaff
      >
        <FormPrompt hasUnsavedChanges={isFormDirty} />
        {autopilotEntityDetailsPending ||
        autopilotEntityDetailsError ||
        isInitialisingEntityDetailsForm ? (
          <AgxEmptyState
            variant="minimal"
            label={
              autopilotEntityDetailsPending || isInitialisingEntityDetailsForm
                ? 'Loading'
                : 'Unable to load licensee details'
            }
          />
        ) : (
          <AgxSpacer direction="vertical" gap="md" fill>
            <AgxSpacer
              direction="horizontal"
              fill
              className="borderBottomContainer"
            >
              <AgxSpacer
                direction="horizontal"
                className="growOne"
                contentJustification="start"
                contentAlignment="center"
                gap="sm"
              >
                <AgxTypography
                  id="staff-license-entity-details"
                  type="label"
                  size="large"
                >
                  Licensee Details
                </AgxTypography>
              </AgxSpacer>

              <AgxSpacer
                direction="horizontal"
                className="growOne"
                contentJustification="end"
                contentAlignment="center"
                gap="sm"
              >
                <AgxSaveChangesButton
                  id="btnSaveChanges"
                  text="Save Changes"
                  variant="primary"
                  size="medium"
                  state={saveChangesState}
                  onClick={() => {
                    void onSaveChanges()
                  }}
                  disabled={updateAutopilotEntityDetailsPending || !isFormDirty}
                />
              </AgxSpacer>
            </AgxSpacer>

            <AgxTab
              options={tabOptions}
              onChange={(tab) => setSelectedTab(tab.id as EntityDetailsTab)}
            />

            <AgxSpacer
              key={selectedTab}
              direction="vertical"
              gap="md"
              className="container50Percent"
            >
              <AgxSpacer direction="vertical" gap="md">
                <AgxSpacer className="borderBottomContainer">
                  <AgxTypography
                    id="staff-license-company-details"
                    type="title"
                    size="large"
                  >
                    {selectedTabLabel} Company Details
                  </AgxTypography>
                </AgxSpacer>

                <AgxSpacer direction="vertical" gap="md">
                  <AgxSpacer direction="horizontal">
                    <AgxTextInput
                      id="tradingName"
                      label="Trading Name"
                      type="text"
                      stretch
                      defaultValue={selectedEntityDetails.tradingName}
                      onChange={(value) =>
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          tradingName: value,
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>

                  <AgxSpacer direction="horizontal">
                    <AgxTextInput
                      id="licenseeName"
                      label="Licensee name"
                      type="text"
                      defaultValue={companyDetails?.licenseeName}
                      stretch
                      onChange={(value) =>
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          companyDetails: {
                            ...prev.companyDetails,
                            licenseeName: value,
                          },
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>

                  <AgxSpacer direction="horizontal" gap="sm">
                    <AgxTextInput
                      id="abn"
                      label="ABN"
                      type="abn"
                      defaultValue={companyDetails?.abn}
                      stretch
                      onChange={(value) =>
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          companyDetails: {
                            ...prev.companyDetails,
                            abn: value,
                          },
                        }))
                      }
                      optionalText=""
                    />

                    <AgxTextInput
                      id="acn"
                      label="ACN"
                      type="acn"
                      defaultValue={companyDetails?.acn}
                      stretch
                      onChange={(value) =>
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          companyDetails: {
                            ...prev.companyDetails,
                            acn: value,
                          },
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>

                  {licences.map((licence, index) => (
                    <AgxSpacer
                      key={`${licence.state ?? LicenceDetailsState.ACT}-${index}`}
                      direction="vertical"
                      gap="md"
                    >
                      <AgxSpacer
                        direction="horizontal"
                        contentJustification="space-between"
                        contentAlignment="center"
                        className="borderBottomContainer"
                      >
                        <AgxTypography
                          id={`staff-license-licence-heading-${index}`}
                          type="label"
                          size="medium"
                        >
                          {`Licence ${index + 1}`}
                        </AgxTypography>
                        {licences.length > 1 && (
                          <AgxButton
                            id={`remove-licence-${index}`}
                            text="Remove"
                            size="small"
                            variant="hollow"
                            onClick={() => removeLicence(index)}
                          />
                        )}
                      </AgxSpacer>

                      <AgxSpacer direction="horizontal" gap="sm">
                        <AgxTextInput
                          id={`licenceNumber-${index}`}
                          label="Licence Number"
                          type="text"
                          defaultValue={licence.licenceNumber ?? ''}
                          stretch
                          onChange={(value) => {
                            updateLicence(index, {
                              ...licence,
                              licenceNumber: value,
                            })
                          }}
                          optionalText=""
                        />

                        <AgxDatePicker
                          id={`dtExpiryDate-${index}`}
                          label="Expiry Date"
                          stretch
                          value={licence.expiryDate ?? null}
                          onChange={(value) => {
                            updateLicence(index, {
                              ...licence,
                              expiryDate: toDateOnlyString(value),
                            })
                          }}
                          optionalText=""
                        />
                      </AgxSpacer>

                      <AgxSpacer direction="horizontal">
                        <AgxDropdown
                          id={`licenceState-${index}`}
                          label="State"
                          selected={
                            licence.state?.toString() ??
                            LicenceDetailsState.ACT.toString()
                          }
                          onChange={(value) => {
                            updateLicence(index, {
                              ...licence,
                              state: value as LicenceDetailsState,
                            })
                          }}
                          options={getLicenceStateOptions(licences, index)}
                        />
                      </AgxSpacer>
                    </AgxSpacer>
                  ))}

                  <AgxSpacer>
                    <AgxButton
                      id="add-licence"
                      text="Add Licence"
                      variant="primary"
                      size="medium"
                      disabled={
                        licences.length ===
                        Object.values(LicenceDetailsState).length
                      }
                      onClick={addLicence}
                    />
                  </AgxSpacer>
                </AgxSpacer>
              </AgxSpacer>

              <AgxSpacer direction="vertical" gap="md">
                <AgxSpacer className="borderBottomContainer">
                  <AgxTypography
                    id="staff-license-contact-details"
                    type="title"
                    size="large"
                  >
                    {selectedTabLabel} Contact Details
                  </AgxTypography>
                </AgxSpacer>

                <AgxSpacer direction="vertical" gap="md">
                  <AgxSpacer direction="horizontal" gap="sm">
                    <AgxTextInput
                      id="listingsEmail"
                      label="Listings Email"
                      type="email"
                      defaultValue={selectedEntityDetails.listingsEmail}
                      stretch
                      onInputValueChange={({ value }) =>
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          listingsEmail: value,
                        }))
                      }
                      optionalText=""
                    />

                    <AgxTextInput
                      id="contractEmail"
                      label="Contract Email"
                      type="email"
                      defaultValue={selectedEntityDetails.contractsEmail}
                      stretch
                      onInputValueChange={({ value }) =>
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          contractsEmail: value,
                        }))
                      }
                      optionalText=""
                    />

                    <AgxTextInput
                      id="complianceEmail"
                      label="Compliance Email"
                      type="email"
                      defaultValue={selectedEntityDetails.complianceEmail}
                      stretch
                      onInputValueChange={({ value }) =>
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          complianceEmail: value,
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>

                  <AgxSpacer direction="horizontal">
                    <AgxTextInput
                      id="phoneNumber"
                      label="Listings Phone Number"
                      type="text"
                      defaultValue={selectedEntityDetails.listingsPhoneNumber}
                      stretch
                      onChange={(value) =>
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          listingsPhoneNumber: value,
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>

                  <AgxSpacer direction="horizontal">
                    <AgxTextInput
                      id="entityPhoneNumber"
                      label="Entity Phone Number"
                      type="text"
                      defaultValue={
                        selectedEntityDetails.entityPhoneNumber ?? ''
                      }
                      stretch
                      onChange={(value) =>
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          entityPhoneNumber: value,
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>
                </AgxSpacer>
              </AgxSpacer>

              <AgxSpacer direction="vertical" gap="md">
                <AgxSpacer className="borderBottomContainer">
                  <AgxTypography
                    id="staff-license-address"
                    type="title"
                    size="large"
                  >
                    {selectedTabLabel} Address
                  </AgxTypography>
                </AgxSpacer>

                <AgxSpacer direction="vertical" gap="md">
                  <AgxSpacer direction="horizontal">
                    <AgxTextInput
                      id="streetAddress"
                      label="Street Address"
                      type="text"
                      defaultValue={address?.streetAddress}
                      stretch
                      onChange={(value) =>
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            streetAddress: value,
                          },
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>

                  <AgxSpacer direction="horizontal" gap="sm">
                    <AgxTextInput
                      id="suburb"
                      label="Suburb"
                      type="text"
                      defaultValue={address?.suburb}
                      stretch
                      onChange={(value) =>
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            suburb: value,
                          },
                        }))
                      }
                      optionalText=""
                    />

                    <AgxDropdown
                      id="state"
                      label="State"
                      hideOptionalText
                      selected={address?.state}
                      onChange={(value) => {
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            state: value,
                          },
                        }))
                      }}
                      options={stateOptions}
                    />

                    <AgxTextInput
                      id="postcode"
                      label="Postcode"
                      type="text"
                      defaultValue={address?.postalCode}
                      stretch
                      onChange={(value) =>
                        updateSelectedEntityDetails((prev) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            postalCode: value,
                          },
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>
                </AgxSpacer>
              </AgxSpacer>
            </AgxSpacer>
          </AgxSpacer>
        )}
      </ContentLayout>
    </PageLayout>
  )
}

export default StaffLicense
